import React, { useState, useEffect } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { MemberService } from "services/member.service";
import { IMemberResponseDTO, MaritalStatus, IDepartmentFlatDTO } from "models/church.types";

export default function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [member, setMember] = useState<IMemberResponseDTO | null>(null);
  const [loading, setLoading] = useState(true);

  // Fonction pour formater le statut matrimonial
  const getMaritalStatusLabel = (status?: MaritalStatus | null) => {
    switch (status) {
      case "SINGLE":
        return "Célibataire";
      case "MARRIED":
        return "Marié(e)";
      case "DIVORCED":
        return "Divorcé(e)";
      case "WIDOWED":
        return "Veuf/Veuve";
      default:
        return "Non spécifié";
    }
  };

  // Fonction utilitaire pour formater proprement les dates de l'API
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  useEffect(() => {
    const fetchMember = async () => {
      try {
        if (id) {
          const data = await MemberService.getById(parseInt(id));
          setMember(data);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération du membre", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMember();
  }, [id]);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <i className="fas fa-circle-notch animate-spin text-[#D4AF37] text-3xl"></i>
      </div>
    );

  if (!member)
    return (
      <div className="p-10 text-center text-white">Membre introuvable.</div>
    );

  return (
    <div className="flex flex-col w-full min-h-screen mt-3 pt-24 items-center justify-start px-4">
      <div className="w-full max-w-5xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-3xl bg-[#0B1222] border border-slate-700/50 shadow-2xl overflow-hidden backdrop-blur-sm">
          {/* Header */}
          <div className="bg-slate-900/80 px-8 py-6 border-b border-slate-700/50">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-2 h-8 bg-[#D4AF37] rounded-full mr-4 shadow-[0_0_15px_rgba(212,175,55,0.4)]"></div>
                <h6 className="text-white text-base font-black uppercase tracking-widest">
                  Fiche d'identité Membre
                </h6>
              </div>
              <button
                onClick={() => history.goBack()}
                className="bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white p-2 px-4 rounded-lg text-xs font-bold uppercase transition-all border border-slate-700"
              >
                <i className="fas fa-arrow-left mr-2"></i> Retour
              </button>
            </div>
          </div>

          <div className="flex-auto p-8 lg:p-12">
            <div className="flex flex-wrap -mx-4">
              {/* COLONNE GAUCHE : PROFIL & STATUS */}
              <div className="w-full lg:w-4/12 px-4 flex flex-col items-center text-center">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-b from-[#D4AF37] to-transparent rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                  <div className="relative w-64 h-64 mb-6 rounded-2xl border-2 border-slate-700 overflow-hidden bg-slate-900 shadow-2xl flex items-center justify-center">
                    {member?.photoLink ? (
                      <img
                        src={
                          member.photoLink.startsWith("http")
                            ? member.photoLink
                            : `${process.env.REACT_APP_API_URL?.replace(/\/api$/, "")}${member.photoLink}`
                        }
                        className="w-full h-full object-cover object-top transform group-hover:scale-105 transition-transform duration-500"
                        alt="Profil"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <i
                          className={`fas ${member.isChild ? "fa-child text-slate-700" : "fa-user text-slate-800"} text-8xl`}
                        ></i>
                        <span className="text-slate-600 text-[10px] mt-4 font-black uppercase tracking-widest">
                          Aucune Photo
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <h3 className="text-3xl font-black text-white uppercase tracking-tighter mb-1">
                  {member.firstName}
                </h3>
                <h3 className="text-2xl font-light text-[#D4AF37] uppercase mb-4">
                  {member.lastName}
                </h3>

                <div className="w-16 h-1 bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent mb-6"></div>

                {/* Badge Dynamique Adulte vs Enfant */}
                {member.isChild ? (
                  <div className="px-6 py-2 bg-cyan-950/40 text-cyan-400 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] border border-cyan-500/20 shadow-inner">
                    <i className="fas fa-child mr-2"></i>Enfant (Écodime)
                  </div>
                ) : (
                  <div className="px-6 py-2 bg-slate-900/80 text-[#D4AF37] rounded-xl text-[10px] font-black uppercase tracking-[0.2em] border border-[#D4AF37]/20 shadow-inner">
                    <i className="fas fa-user-tie mr-2"></i>
                    {member.departments && member.departments.length > 0
                      ? member.departments.length === 1
                        ? member.departments[0].name
                        : `${member.departments.length} Départements`
                      : "Membre Adulte"}
                  </div>
                )}
              </div>

              <div className="hidden lg:block w-[1px] bg-gradient-to-b from-transparent via-slate-700/50 to-transparent mx-4"></div>

              {/* COLONNE DROITE : INFOS GÉNÉRALES */}
              <div className="w-full lg:flex-1 px-8 mt-12 lg:mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                  {/* Email */}
                  <div className="relative group">
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-3 block tracking-[0.15em] opacity-80">
                      <i className="fas fa-envelope mr-3"></i>Email
                    </label>
                    <p className="text-white text-lg font-semibold tracking-wide">
                      {member.email || "—"}
                    </p>
                    <div className="mt-3 h-[1px] w-full bg-slate-700/40">
                      <div className="h-full w-12 bg-[#D4AF37]/40 group-hover:w-full transition-all duration-700"></div>
                    </div>
                  </div>

                  {/* Téléphone */}
                  <div className="relative group">
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-3 block tracking-[0.15em] opacity-80">
                      <i className="fas fa-phone mr-3"></i>Numéro de Téléphone
                    </label>
                    <p className="text-white text-lg font-semibold tracking-wide">
                      {member.phoneNumber || "—"}
                    </p>
                    <div className="mt-3 h-[1px] w-full bg-slate-700/40">
                      <div className="h-full w-12 bg-[#D4AF37]/40 group-hover:w-full transition-all duration-700"></div>
                    </div>
                  </div>

                  {/* Sexe */}
                  <div className="relative group">
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-3 block tracking-[0.15em] opacity-80">
                      <i
                        className={`fas ${member.gender === "MALE" ? "fa-mars text-blue-400" : member.gender === "FEMALE" ? "fa-venus text-pink-400" : "fa-venus-mars"} mr-3`}
                      ></i>
                      Sexe / Genre
                    </label>
                    <p className="text-white text-lg font-semibold tracking-wide">
                      {member.gender === "MALE"
                        ? "Masculin"
                        : member.gender === "FEMALE"
                          ? "Féminin"
                          : "Non spécifié"}
                    </p>
                    <div className="mt-3 h-[1px] w-full bg-slate-700/40">
                      <div className="h-full w-12 bg-[#D4AF37]/40 group-hover:w-full transition-all duration-700"></div>
                    </div>
                  </div>

                  {/* Date de Naissance */}
                  <div className="relative group">
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-3 block tracking-[0.15em] opacity-80">
                      <i className="fas fa-birthday-cake mr-3"></i>Date de
                      Naissance
                    </label>
                    <p className="text-white text-lg font-semibold tracking-wide">
                      {formatDate(member.birthDate)}
                    </p>
                    <div className="mt-3 h-[1px] w-full bg-slate-700/40">
                      <div className="h-full w-12 bg-[#D4AF37]/40 group-hover:w-full transition-all duration-700"></div>
                    </div>
                  </div>

                  {/* Date d'intégration */}
                  <div className="relative group">
                    <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-3 block tracking-[0.15em] opacity-80">
                      <i className="fas fa-calendar-check mr-3"></i>Membre
                      depuis le
                    </label>
                    <p className="text-white text-lg font-semibold tracking-wide">
                      {formatDate(member.joinDate)}
                    </p>
                    <div className="mt-3 h-[1px] w-full bg-slate-700/40">
                      <div className="h-full w-12 bg-[#D4AF37]/40 group-hover:w-full transition-all duration-700"></div>
                    </div>
                  </div>

                  {/* AFFICHAGE EN CAS D'ADULTE : Situation matrimoniale */}
                  {!member.isChild && (
                    <div className="relative group">
                      <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-3 block tracking-[0.15em] opacity-80">
                        <i className="fas fa-heart text-red-400 mr-3"></i>
                        Situation Matrimoniale
                      </label>
                      <p className="text-white text-lg font-semibold tracking-wide">
                        {getMaritalStatusLabel(member.maritalStatus)}
                      </p>
                      <div className="mt-3 h-[1px] w-full bg-slate-700/40">
                        <div className="h-full w-12 bg-[#D4AF37]/40 group-hover:w-full transition-all duration-700"></div>
                      </div>
                    </div>
                  )}

                  {/* AFFICHAGE EN CAS DE MEMBRE MARIÉ : Date de Mariage */}
                  {!member.isChild && member.maritalStatus === MaritalStatus.MARRIED && (
                    <div className="relative group">
                      <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-3 block tracking-[0.15em] opacity-80">
                        <i className="fas fa-rings-wedding text-amber-400 mr-3"></i>
                        Date de Mariage
                      </label>
                      <p className="text-white text-lg font-semibold tracking-wide">
                        {formatDate(member.marriageDate)}
                      </p>
                      <div className="mt-3 h-[1px] w-full bg-slate-700/40">
                        <div className="h-full w-12 bg-[#D4AF37]/40 group-hover:w-full transition-all duration-700"></div>
                      </div>
                    </div>
                  )}

                  {/* AFFICHAGE EN CAS D'ADULTE : Affectation aux Départements */}
                  {!member.isChild && (
                    <div className="md:col-span-2 relative group">
                      <label className="text-[10px] text-[#D4AF37] uppercase font-black mb-3 block tracking-[0.15em] opacity-80">
                        <i className="fas fa-id-badge mr-3"></i> Assignation
                        Départements
                      </label>
                      {member.departments && member.departments.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {member.departments.map((dept: IDepartmentFlatDTO) => (
                            <span
                              key={dept.id}
                              className="px-4 py-1.5 bg-slate-800 text-[#D4AF37] border border-[#D4AF37]/30 rounded-lg text-xs font-bold uppercase tracking-wider"
                            >
                              {dept.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 italic text-sm mt-1">
                          Aucun service assigné
                        </p>
                      )}
                      <div className="mt-3 h-[1px] w-full bg-slate-700/40"></div>
                    </div>
                  )}

                  {/* AFFICHAGE EN CAS D'ENFANT : Responsable Légal / Parent rattaché */}
                  {member.isChild && (
                    <>
                      <div className="md:col-span-2 relative group bg-[#0F172A]/40 p-4 rounded-xl border border-slate-800">
                        <label className="text-[10px] text-cyan-400 uppercase font-black mb-2 block tracking-[0.15em]">
                          <i className="fas fa-user-shield mr-3"></i> Parent /
                          Tuteur Légal Responsable
                        </label>
                        {member.parentId ? (
                          <Link
                            to={`/admin/members/${member.parentId}`}
                            className="inline-flex items-center text-white hover:text-[#D4AF37] transition-colors font-bold text-lg group/link"
                          >
                            <i className="fas fa-link mr-2 text-xs text-slate-500 group-hover/link:text-[#D4AF37]"></i>
                            {`${member.parentLastName || ""} ${member.parentFirstName || ""}`.trim()}
                          </Link>
                        ) : (
                          <p className="text-slate-500 italic text-sm">
                            Aucun parent référencé
                          </p>
                        )}
                      </div>

                      {/* Classe Écodime */}
                      <div className="md:col-span-2 relative group">
                        <label className="text-[10px] text-cyan-400 uppercase font-black mb-3 block tracking-[0.15em]">
                          <i className="fas fa-school mr-3"></i> Classe Écodime
                          Dimanche
                        </label>
                        <p className="text-white text-xl font-black uppercase italic tracking-tighter">
                          {member.ecodimeClassName ||
                            "Non spécifiée / Non assignée"}
                        </p>
                        <div className="mt-3 h-[1px] w-full bg-slate-700/40"></div>
                      </div>
                    </>
                  )}
                </div>

                {/* Actions pied de page */}
                <div className="flex justify-start mt-12 gap-4">
                  <button
                    onClick={() =>
                      history.push(`/admin/members/edit/${member.id}`)
                    }
                    className="group relative bg-[#D4AF37] text-slate-900 px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] overflow-hidden transition-all hover:shadow-[0_10px_30px_rgba(212,175,55,0.3)] active:scale-95"
                  >
                    <div className="relative z-10 flex items-center">
                      <i className="fas fa-edit mr-3 text-sm"></i> Modifier le
                      profil
                    </div>
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}